import React, { useState } from 'react'
import { isListingDurationAvailable } from '@popety-io/utils'
import { getDateLocale } from '../events/formatUtils'
import { ListingEvent } from '../events/event.types'
import { MetricsDisplay } from '../../MetricsDisplay'
import { PropertyDetails } from '../../PropertyDetails'
import { ImageGallery } from '../../ImageGallery'
import { ContactCard } from '../../../organisms/ContactCard'
import { FeaturesList } from '../../FeaturesList'
import { Section } from '../../../atoms/Section'
import { SectionHeaderWithIcon as SectionHeader } from '../../SectionHeaderWithIcon'
import { Icon } from '../../../atoms/Icon'
import { Box } from '../../../atoms/Box'
import { Badge } from '../../../atoms/Badge'
import {
  PropertyTitle,
  PropertyAddress,
  MainPrice,
  PricePerMeter,
  Description,
  ListingMetaCard,
  ListingMetaItem,
  ListingMetaLabel,
  ListingMetaValue,
  HeroSection,
} from './EventDetail.styles'

interface ListingDetailProps {
  event: ListingEvent
  formatPrice?: (price: number) => string
  formatPricePerSqm?: (price: number) => string
  formatDate?: (date: string | Date) => string
  translations?: any
}

export const ListingDetail: React.FC<ListingDetailProps> = ({
  event,
  formatPrice = (price) => {
    // For rental, show per month; for sale, show total price
    return event.type === 'rental'
      ? `CHF ${price.toLocaleString()}/m`
      : `CHF ${price.toLocaleString()}`
  },
  formatPricePerSqm = (price) => `CHF ${price.toLocaleString()}/m²`,
  formatDate = (date) =>
    new Date(date).toLocaleDateString(getDateLocale(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  translations,
}) => {
  const isRental = event.type === 'rental'
  const isSale = event.type === 'sale'

  // State for image carousel
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const formatArea = (area?: number) => {
    if (!area || area <= 0) return null
    return `${area} m²`
  }

  const formatRooms = (rooms?: number) => {
    if (!rooms || rooms <= 0) return null
    return rooms.toString()
  }

  const formatBathrooms = (bathrooms?: number) => {
    if (!bathrooms || bathrooms <= 0) return null
    return bathrooms.toString()
  }

  // Determine property title
  const propertyTitle =
    event.propertyType ||
    event.propertyCategory ||
    (isRental
      ? translations?.propertyForRent || 'Property for Rent'
      : translations?.propertyForSale || 'Property for Sale')

  // Determine online duration field
  const onlineDuration = event.onlineDays

  return (
    <>
      {/* Quick Info Section */}
      <Box>
        <PropertyTitle>{propertyTitle}</PropertyTitle>

        <PropertyAddress>
          <Icon name="location" size="small" />
          {event.address ||
            translations?.addressNotSpecified ||
            'Address not specified'}
        </PropertyAddress>

        {/* Image Carousel */}
        {event.imageUrls && event.imageUrls.length > 0 && (
          <HeroSection
            style={{ marginTop: '16px', marginBottom: '16px', height: '300px' }}
          >
            <ImageGallery
              images={event.imageUrls}
              currentIndex={currentImageIndex}
              onIndexChange={setCurrentImageIndex}
              height="100%"
            />
          </HeroSection>
        )}

        <Box display="flex" alignItems="center" gap="16px" marginTop="16px">
          <MainPrice>{formatPrice(event.price)}</MainPrice>
          {event.pricePerSqm && (
            <PricePerMeter>
              {formatPricePerSqm(event.pricePerSqm)}
            </PricePerMeter>
          )}
        </Box>

        {/* Listing Meta */}
        <ListingMetaCard>
          <ListingMetaItem>
            <ListingMetaLabel>
              {translations?.published || 'Published'}
            </ListingMetaLabel>
            <ListingMetaValue>{formatDate(event.date)}</ListingMetaValue>
          </ListingMetaItem>

          <ListingMetaItem>
            <ListingMetaLabel>
              {translations?.statusLabel || 'Status'}
            </ListingMetaLabel>
            <Badge variant={event.ad_status ? 'success' : 'danger'}>
              {event.ad_status
                ? translations?.active || 'Active'
                : translations?.inactive || 'Inactive'}
            </Badge>
          </ListingMetaItem>

          {isListingDurationAvailable(onlineDuration) && (
            <ListingMetaItem>
              <ListingMetaLabel>
                {translations?.onlineFor || 'Online for'}
              </ListingMetaLabel>
              <ListingMetaValue>
                {String(onlineDuration)} {translations?.days || 'days'}
              </ListingMetaValue>
            </ListingMetaItem>
          )}
        </ListingMetaCard>

        <Box marginTop="24px" marginBottom="24px">
          <MetricsDisplay
            metrics={
              [
                formatRooms(event.rooms) && {
                  value: formatRooms(event.rooms)!,
                  label: translations?.rooms || 'Rooms',
                },
                formatArea(event.area) && {
                  value: formatArea(event.area)!,
                  label: translations?.livingArea || 'Living Area',
                },
                isSale &&
                  'landArea' in event &&
                  formatArea(event.landArea) && {
                    value: formatArea(event.landArea)!,
                    label: translations?.landArea || 'Land Area',
                  },
                formatBathrooms(event.bathrooms) && {
                  value: formatBathrooms(event.bathrooms)!,
                  label: translations?.bathrooms || 'Bathrooms',
                },
                event.floor && {
                  value: event.floor,
                  label: translations?.floor || 'Floor',
                },
                event.constructionYear &&
                  event.constructionYear > 0 && {
                    value: event.constructionYear.toString(),
                    label: translations?.built || 'Built',
                  },
              ].filter(Boolean) as any[]
            }
            columns={5}
          />
        </Box>
      </Box>

      {/* Description Section */}
      {event.description && (
        <Section>
          <SectionHeader icon="document-text">
            {translations?.description || 'Description'}
          </SectionHeader>
          <Description
            dangerouslySetInnerHTML={{ __html: event.description }}
          />
        </Section>
      )}

      {/* Property Details */}
      <Section>
        <SectionHeader icon="info">
          {translations?.propertyDetails || 'Property Details'}
        </SectionHeader>
        <PropertyDetails
          details={[
            {
              label: translations?.propertyType || 'Property Type',
              value: event.propertyType || null,
            },
            {
              label: translations?.propertyCategory || 'Property Category',
              value: event.propertyCategory || null,
            },
            {
              label: translations?.constructionYear || 'Construction Year',
              value: event.constructionYear || null,
            },
            ...(isSale && 'renovationYear' in event && event.renovationYear
              ? [
                  {
                    label: translations?.renovationYear || 'Renovation Year',
                    value: event.renovationYear,
                  },
                ]
              : []),
            {
              label: translations?.livingArea || 'Living Area',
              value: formatArea(event.area),
            },
            ...(isSale && 'landArea' in event && event.landArea
              ? [
                  {
                    label: translations?.landArea || 'Land Area',
                    value: formatArea(event.landArea),
                  },
                ]
              : []),
            {
              label: translations?.rooms || 'Rooms',
              value: formatRooms(event.rooms) || null,
            },
            {
              label: translations?.bathrooms || 'Bathrooms',
              value: formatBathrooms(event.bathrooms) || null,
            },
            {
              label: translations?.floor || 'Floor',
              value: event.floor || null,
            },
            ...(isRental && 'availableDate' in event
              ? [
                  {
                    label: translations?.available || 'Available',
                    value: event.availableDate
                      ? formatDate(event.availableDate)
                      : translations?.immediately || 'Immediately',
                  },
                ]
              : []),
            ...(isRental && 'furnished' in event
              ? [
                  {
                    label: translations?.furnished || 'Furnished',
                    value: event.furnished || null,
                  },
                ]
              : []),
            ...(isRental && 'petsAllowed' in event
              ? [
                  {
                    label: translations?.petsAllowed || 'Pets Allowed',
                    value: event.petsAllowed || null,
                  },
                ]
              : []),
            {
              label: translations?.energyLabel || 'Energy Label',
              value: event.energyLabel || null,
            },
            ...(isSale && 'heatingType' in event
              ? [
                  {
                    label: translations?.heatingType || 'Heating Type',
                    value: event.heatingType || null,
                  },
                ]
              : []),
          ].filter((d) => d.value !== null && d.value !== '—')}
        />
      </Section>

      {/* Contact Card Section */}
      {event.brokersInfo?.contact_names && (
        <Section>
          <ContactCard
            title={translations?.propertyAgents || 'Property Agents'}
            contacts={
              event.brokersInfo?.contact_names &&
              event.brokersInfo.contact_names.length > 0
                ? event.brokersInfo.contact_names.map((contact: any) => ({
                    name: contact.name,
                    type: contact.type as 'individual' | 'professional',
                  }))
                : []
            }
            mandateType={event.brokersInfo?.mandate}
          />
        </Section>
      )}

      {/* Features & Amenities Section */}
      {(() => {
        const features = []

        // Add boolean amenities as features with proper icons
        if (event.parking)
          features.push({
            icon: 'car' as const,
            text: translations?.parking || 'Parking',
          })
        if (event.balcony)
          features.push({
            icon: 'sun' as const,
            text: translations?.balcony || 'Balcony',
          })
        if (event.terrace)
          features.push({
            icon: 'home' as const,
            text: translations?.terrace || 'Terrace',
          })
        if (event.garden)
          features.push({
            icon: 'leaf' as const,
            text: translations?.garden || 'Garden',
          })
        if (isSale && 'garage' in event && event.garage)
          features.push({
            icon: 'garage' as const,
            text: translations?.garage || 'Garage',
          })
        if (isSale && 'elevator' in event && event.elevator)
          features.push({
            icon: 'arrow-up' as const,
            text: translations?.elevator || 'Elevator',
          })
        if (isRental && 'furnished' in event && event.furnished)
          features.push({
            icon: 'check' as const,
            text: translations?.furnished || 'Furnished',
          })
        if (isRental && 'petsAllowed' in event && event.petsAllowed)
          features.push({
            icon: 'check' as const,
            text: translations?.petsAllowed || 'Pets Allowed',
          })

        // Add any additional features from event.features if it exists
        // Assuming event.features is an array of strings, we need to convert them
        const eventFeatures =
          'features' in event && event.features
            ? event.features.map((f: string) => ({
                icon: 'check' as const,
                text: f,
              }))
            : []

        const allFeatures = [...features, ...eventFeatures]

        return allFeatures.length > 0 ? (
          <Section>
            <SectionHeader icon="star">
              {translations?.featuresAndAmenities || 'Features & Amenities'}
            </SectionHeader>
            <FeaturesList features={allFeatures} />
          </Section>
        ) : null
      })()}
    </>
  )
}
